import { Loading } from 'notiflix'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import { Report } from 'notiflix/build/notiflix-report-aio'
import React, { useEffect, useMemo, useState } from 'react'
import { FaChevronDown, FaChevronRight, FaFilePdf, FaSyncAlt } from 'react-icons/fa'
import { useAuthUserData } from '../../../authUserProvider'
import api, { apiUpload } from '../../../helpers/apiRequester'
import { FieldEditor } from './fieldEditor'
import styles from './styles.module.scss'
import {
    FIELD_TYPE_LABELS,
    TemplateField,
    TemplateFieldType,
    TemplateRow,
    formatDateTime,
    hasSignatureField,
} from './types'

interface VaultTabProps {
    templates: TemplateRow[]
    loaded: boolean
    onRefresh: () => Promise<void> | void
    onTemplatesChange: (updater: (prev: TemplateRow[]) => TemplateRow[]) => void
    onSendTemplate: (templateId: number) => void
    /** Template to open in the field editor as soon as the vault renders. */
    editTemplateId?: number | null
    onEditHandled?: () => void
}

/** Collapses repeated field types into "Name ×2"-style chips, signature last. */
const summariseFields = (fields: TemplateField[]) => {
    const counts = new Map<TemplateFieldType, number>()
    fields.forEach((f) => counts.set(f.type, (counts.get(f.type) || 0) + 1))
    return Array.from(counts.entries())
        .sort(([a], [b]) => (a === 'signature' ? 1 : b === 'signature' ? -1 : 0))
        .map(([type, count]) => ({
            type,
            label: count > 1 ? `${FIELD_TYPE_LABELS[type]} ×${count}` : FIELD_TYPE_LABELS[type],
        }))
}

const pageCount = (fields: TemplateField[]) => new Set(fields.map((f) => f.page)).size

export const VaultTab = ({
    templates,
    loaded,
    onRefresh,
    onTemplatesChange,
    onSendTemplate,
    editTemplateId = null,
    onEditHandled,
}: VaultTabProps) => {
    const authUser = useAuthUserData()

    // null = untouched: open by default only when the vault is empty.
    const [uploadToggled, setUploadToggled] = useState<boolean | null>(null)
    const uploadOpen = uploadToggled ?? (loaded && templates.length === 0)
    const setUploadOpen = (next: boolean) => setUploadToggled(next)
    const [uploadName, setUploadName] = useState('')
    const [uploadFile, setUploadFile] = useState<File | null>(null)
    const [fileInputKey, setFileInputKey] = useState(0)
    const [search, setSearch] = useState('')
    const [selectedId, setSelectedId] = useState<number | null>(null)

    const [editingTemplate, setEditingTemplate] = useState<TemplateRow | null>(null)

    // Open the editor straight away when another tab asked for a specific template.
    useEffect(() => {
        if (!editTemplateId) return
        const target = templates.find((t) => t.id === editTemplateId)
        if (target) {
            setEditingTemplate(target)
            onEditHandled?.()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editTemplateId, templates])

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase()
        if (!q) return templates
        return templates.filter(
            (t) => t.name.toLowerCase().includes(q) || (t.created_by || '').toLowerCase().includes(q),
        )
    }, [templates, search])

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null
        if (file && file.type !== 'application/pdf') {
            Report.failure('Invalid file', 'Please choose a PDF document.', 'Okay')
            return
        }
        setUploadFile(file)
        if (file && !uploadName.trim()) setUploadName(file.name.replace(/\.pdf$/i, ''))
    }

    const handleUpload = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (!uploadFile) {
            Report.failure('Error', 'Please choose the contract PDF.', 'Okay')
            return
        }
        if (!uploadName.trim()) {
            Report.failure('Error', 'Please give the template a name.', 'Okay')
            return
        }

        Loading.circle('Uploading template...')
        try {
            const fd = new FormData()
            fd.append('template', uploadFile)
            fd.append('name', uploadName.trim())
            fd.append('created_by', authUser?.email ?? '')

            const response = await apiUpload('contract-sign/templates/upload', fd)
            Loading.remove()
            if (!response.data?.success) {
                return Report.failure('Error', response.data?.message || 'An error occurred.', 'Okay')
            }

            const name = uploadName.trim()
            setUploadName('')
            setUploadFile(null)
            setFileInputKey((k) => k + 1)
            setUploadOpen(false)
            await onRefresh()

            // Go straight into the editor so the new template gets its fields.
            setEditingTemplate({ id: response.data.template_id, name, fields: [], created_by: null, created_at: null })
        } catch (error: any) {
            Loading.remove()
            const message = error?.response?.data?.message || 'An error occurred while uploading the template.'
            Report.failure('Error', message, 'Okay')
        }
    }

    const archiveTemplate = (template: TemplateRow) => {
        Confirm.show(
            'Archive template',
            `Archive "${template.name}"? It will disappear from the template list but previously sent contracts keep their copy.`,
            'Archive',
            'Cancel',
            async () => {
                Loading.circle('Archiving template...')
                try {
                    const response = await api({
                        url: 'contract-sign/templates/archive',
                        data: { template_id: template.id },
                    })
                    Loading.remove()
                    if (!response.data?.success) {
                        return Report.failure('Error', response.data?.message || 'An error occurred.', 'Okay')
                    }
                    setSelectedId(null)
                    onRefresh()
                } catch (error: any) {
                    Loading.remove()
                    const message = error?.response?.data?.message || 'An error occurred while archiving.'
                    Report.failure('Error', message, 'Okay')
                }
            },
        )
    }

    if (editingTemplate) {
        return (
            <FieldEditor
                templateId={editingTemplate.id}
                templateName={editingTemplate.name}
                onClose={() => setEditingTemplate(null)}
                onSaved={(fields) => {
                    onTemplatesChange((prev) => prev.map((t) => (t.id === editingTemplate.id ? { ...t, fields } : t)))
                    setEditingTemplate(null)
                }}
            />
        )
    }

    const renderDetail = (template: TemplateRow) => {
        const ready = template.fields.length > 0
        const signature = hasSignatureField(template.fields)
        const stats: { label: string; value: string }[] = [
            { label: 'Fields', value: ready ? String(template.fields.length) : 'None yet' },
            { label: 'Pages with fields', value: ready ? String(pageCount(template.fields)) : '—' },
            { label: 'Delivery', value: !ready ? '—' : signature ? 'Signing link' : 'PDF attachment' },
            { label: 'Uploaded', value: formatDateTime(template.created_at) },
            { label: 'Uploaded by', value: template.created_by || '—' },
        ]
        return (
            <div className={styles.miniView}>
                <div className={styles.miniHeader}>
                    <h4 className={styles.miniTitle}>{template.name}</h4>
                    <span className={styles.miniMeta}>
                        {!ready
                            ? 'Add fields before this template can be sent'
                            : signature
                              ? 'Recipients receive a personal signing link'
                              : 'Recipients receive the populated PDF by email'}
                    </span>
                </div>
                <div className={styles.miniStats}>
                    {stats.map((stat) => (
                        <div key={stat.label} className={styles.miniStat}>
                            <span className={styles.miniStatValue}>{stat.value}</span>
                            <span className={styles.miniStatLabel}>{stat.label}</span>
                        </div>
                    ))}
                </div>
                {ready && (
                    <>
                        <p className={styles.miniHeading}>Field layout</p>
                        <div className={styles.chipRow}>
                            {summariseFields(template.fields).map((chip) => (
                                <span
                                    key={chip.type}
                                    className={`${styles.chip} ${chip.type === 'signature' ? styles.signature : ''}`}
                                >
                                    {chip.label}
                                </span>
                            ))}
                        </div>
                    </>
                )}
                <div className={styles.miniActions}>
                    <button
                        type="button"
                        className={styles.primaryBtn}
                        disabled={!ready}
                        onClick={() => onSendTemplate(template.id)}
                    >
                        Send this template
                    </button>
                    <button type="button" className={styles.secondaryBtn} onClick={() => setEditingTemplate(template)}>
                        {ready ? 'Edit fields' : 'Add fields'}
                    </button>
                    <button type="button" className={styles.dangerBtn} onClick={() => archiveTemplate(template)}>
                        Archive
                    </button>
                </div>
            </div>
        )
    }

    return (
        <>
            <section className={styles.panel}>
                <button
                    type="button"
                    className={styles.panelToggle}
                    aria-expanded={uploadOpen}
                    onClick={() => setUploadOpen(!uploadOpen)}
                >
                    <span className={styles.panelHeaderText}>
                        <span className={styles.panelTitle}>Upload a contract</span>
                        <span className={styles.panelBody}>
                            Add a blank PDF once, mark where the recipient details and signature go, then reuse it for
                            any number of sends.
                        </span>
                    </span>
                    <FaChevronDown className={`${styles.panelChevron} ${uploadOpen ? styles.open : ''}`} />
                </button>

                {uploadOpen && (
                    <form className={styles.panelContent} onSubmit={handleUpload}>
                        <div className={styles.formStack}>
                            <div className={styles.field}>
                                <label className={styles.fieldLabel}>Template name</label>
                                <input
                                    className={styles.input}
                                    type="text"
                                    value={uploadName}
                                    onChange={(e) => setUploadName(e.target.value)}
                                    placeholder="e.g. Client Care Letter"
                                />
                            </div>
                            <div className={styles.field}>
                                <label className={styles.fieldLabel}>Contract PDF</label>
                                <label className={`${styles.filePicker} ${uploadFile ? styles.hasFile : ''}`}>
                                    <FaFilePdf />
                                    {uploadFile ? uploadFile.name : 'Choose a PDF…'}
                                    <input
                                        key={fileInputKey}
                                        type="file"
                                        accept="application/pdf"
                                        onChange={handleFileChange}
                                    />
                                </label>
                            </div>
                            <div className={styles.rowActions}>
                                <button type="submit" className={styles.primaryBtn}>
                                    Upload template
                                </button>
                            </div>
                        </div>
                    </form>
                )}
            </section>

            <div className={styles.toolbar}>
                <input
                    className={styles.searchInput}
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Filter by template name or uploader…"
                />
                <button type="button" className={styles.secondaryBtn} onClick={() => onRefresh()}>
                    <FaSyncAlt /> Refresh
                </button>
            </div>

            <p className={styles.resultLine}>
                Showing {filtered.length} of {templates.length} templates
            </p>

            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Template</th>
                            <th>Fields</th>
                            <th>Delivery</th>
                            <th>Uploaded</th>
                            <th aria-label="Send" />
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr>
                                <td colSpan={5}>
                                    <div className={styles.emptyState}>
                                        {!loaded
                                            ? 'Loading…'
                                            : templates.length === 0
                                              ? 'No contract templates yet — upload one above to get started.'
                                              : 'No templates match this filter.'}
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filtered.map((template) => {
                                const ready = template.fields.length > 0
                                const signature = hasSignatureField(template.fields)
                                const isOpen = selectedId === template.id
                                return (
                                    <React.Fragment key={template.id}>
                                        <tr
                                            className={isOpen ? styles.selected : ''}
                                            onClick={() =>
                                                setSelectedId((current) =>
                                                    current === template.id ? null : template.id,
                                                )
                                            }
                                        >
                                            <td>
                                                <div className={styles.stackCell}>
                                                    <span className={styles.stackPrimary}>
                                                        <FaChevronRight
                                                            className={`${styles.rowChevron} ${isOpen ? styles.rowChevronOpen : ''}`}
                                                        />
                                                        {template.name}
                                                    </span>
                                                    <span className={styles.stackSecondary}>
                                                        Template #{template.id}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className={styles.stackCell}>
                                                    <span
                                                        className={`${styles.stackPrimary} ${ready ? '' : styles.muted}`}
                                                    >
                                                        {ready
                                                            ? `${template.fields.length} field${template.fields.length === 1 ? '' : 's'}`
                                                            : 'No fields yet'}
                                                    </span>
                                                    {ready && (
                                                        <span className={styles.stackSecondary}>
                                                            {pageCount(template.fields)} page
                                                            {pageCount(template.fields) === 1 ? '' : 's'}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                {!ready ? (
                                                    <span className={`${styles.statusPill} ${styles.muted}`}>
                                                        Not ready
                                                    </span>
                                                ) : (
                                                    <span className={`${styles.pill} ${signature ? '' : styles.muted}`}>
                                                        {signature ? 'Signing link' : 'Attachment'}
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <div className={styles.stackCell}>
                                                    <span className={`${styles.stackPrimary} ${styles.nowrap}`}>
                                                        {formatDateTime(template.created_at)}
                                                    </span>
                                                    <span className={styles.stackSecondary}>
                                                        {template.created_by || '—'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    type="button"
                                                    className={`${styles.secondaryBtn} ${styles.compactBtn}`}
                                                    disabled={!ready}
                                                    title={ready ? 'Send this template' : 'Add fields before sending'}
                                                    onClick={() => onSendTemplate(template.id)}
                                                >
                                                    Send
                                                </button>
                                            </td>
                                        </tr>
                                        {isOpen && (
                                            <tr className={styles.expandedRow}>
                                                <td colSpan={5}>{renderDetail(template)}</td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                )
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </>
    )
}
